/**
 * Hotspot Diagram section behavior.
 * Vanilla JS, no dependencies. Handles:
 *  - showing/positioning the product panel (popup or spotlight mode)
 *  - image carousel inside the panel
 *  - quantity stepper + AJAX add to cart
 *  - a theme-editor-only "position finder" to read X/Y % off clicks
 */
(function () {
  'use strict';

  function init(root) {
    var mode = root.getAttribute('data-mode');
    var stage = root.querySelector('[data-hotspot-stage]');
    var panel = root.querySelector('[data-hotspot-panel]');
    var backdrop = root.querySelector('[data-hotspot-backdrop]');
    var pins = Array.prototype.slice.call(root.querySelectorAll('[data-hotspot-pin]'));

    var panelMedia = panel.querySelector('[data-hotspot-panel-media]');
    var panelImage = panel.querySelector('[data-hotspot-panel-image]');
    var panelPrev = panel.querySelector('[data-hotspot-panel-prev]');
    var panelNext = panel.querySelector('[data-hotspot-panel-next]');
    var panelDots = panel.querySelector('[data-hotspot-panel-dots]');
    var panelTitle = panel.querySelector('[data-hotspot-panel-title]');
    var panelPrice = panel.querySelector('[data-hotspot-panel-price]');
    var panelLink = panel.querySelector('[data-hotspot-panel-link]');
    var panelClose = panel.querySelector('[data-hotspot-panel-close]');
    var variantInput = panel.querySelector('[data-hotspot-variant-input]');
    var qtyInput = panel.querySelector('[data-hotspot-qty-input]');
    var qtyMinus = panel.querySelector('[data-hotspot-qty-minus]');
    var qtyPlus = panel.querySelector('[data-hotspot-qty-plus]');
    var addForm = panel.querySelector('[data-hotspot-add-to-cart]');
    var addBtn = panel.querySelector('[data-hotspot-add-to-cart-btn]');
    var addLabel = panel.querySelector('[data-hotspot-add-label]');

    var activePin = null;
    var activeImages = [];
    var activeImageIndex = 0;

    function getData(blockId) {
      var el = root.querySelector('script[data-hotspot-data="' + blockId + '"]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    function renderImages(images, fallback) {
      activeImages = images && images.length ? images : (fallback ? [fallback] : []);
      activeImageIndex = 0;
      updateImage();

      var showArrows = mode === 'spotlight' && activeImages.length > 1;
      panelPrev.hidden = !showArrows;
      panelNext.hidden = !showArrows;

      panelDots.innerHTML = '';
      if (showArrows) {
        activeImages.forEach(function (_, i) {
          var dot = document.createElement('span');
          if (i === 0) dot.className = 'is-active';
          panelDots.appendChild(dot);
        });
      }
    }

    function updateImage() {
      if (!activeImages.length) return;
      panelImage.src = activeImages[activeImageIndex];
      var dots = panelDots.querySelectorAll('span');
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === activeImageIndex);
      });
    }

    panelPrev.addEventListener('click', function () {
      activeImageIndex = (activeImageIndex - 1 + activeImages.length) % activeImages.length;
      updateImage();
    });
    panelNext.addEventListener('click', function () {
      activeImageIndex = (activeImageIndex + 1) % activeImages.length;
      updateImage();
    });

    function positionPopup(pin) {
      // Positioned in fixed (viewport) coordinates, anchored to whichever
      // side of the pin actually has room for it (right, then left, then
      // below, then above), so the card always reads as "attached" to the
      // pin instead of snapping to a corner of the screen when a naive
      // vertical-center calculation wouldn't fit.
      var pinRect = pin.getBoundingClientRect();
      var panelWidth = panel.offsetWidth || 260;
      var panelHeight = panel.offsetHeight || 260;
      var margin = 12;
      var gap = 16;

      var spaceRight = window.innerWidth - pinRect.right;
      var spaceLeft = pinRect.left;
      var spaceBelow = window.innerHeight - pinRect.bottom;
      var spaceAbove = pinRect.top;

      var left, top;

      if (spaceRight >= panelWidth + gap + margin) {
        left = pinRect.right + gap;
        top = pinRect.top + (pinRect.height / 2) - (panelHeight / 2);
      } else if (spaceLeft >= panelWidth + gap + margin) {
        left = pinRect.left - panelWidth - gap;
        top = pinRect.top + (pinRect.height / 2) - (panelHeight / 2);
      } else if (spaceBelow >= panelHeight + gap + margin) {
        top = pinRect.bottom + gap;
        left = pinRect.left + (pinRect.width / 2) - (panelWidth / 2);
      } else if (spaceAbove >= panelHeight + gap + margin) {
        top = pinRect.top - panelHeight - gap;
        left = pinRect.left + (pinRect.width / 2) - (panelWidth / 2);
      } else {
        // Nothing fits cleanly (tiny viewport) — hug whichever side has
        // more room and let the final clamp below keep it on screen.
        left = spaceRight >= spaceLeft ? pinRect.right + gap : pinRect.left - panelWidth - gap;
        top = pinRect.top + (pinRect.height / 2) - (panelHeight / 2);
      }

      // Final safety clamp so the panel never renders partly off-screen.
      top = Math.max(margin, Math.min(top, window.innerHeight - panelHeight - margin));
      left = Math.max(margin, Math.min(left, window.innerWidth - panelWidth - margin));

      panel.style.left = left + 'px';
      panel.style.top = top + 'px';
    }

    function openPanel(pin) {
      var blockId = pin.getAttribute('data-block-id');
      var data = getData(blockId);
      if (!data) return;

      if (activePin) activePin.classList.remove('is-active');
      activePin = pin;
      pin.classList.add('is-active');
      pin.setAttribute('aria-expanded', 'true');

      panelTitle.textContent = data.title;
      panelPrice.innerHTML = data.compareAtPrice
        ? '<s>' + data.compareAtPrice + '</s> ' + data.price
        : data.price;
      panelLink.href = data.url;
      variantInput.value = data.variantId;
      qtyInput.value = 1;
      renderImages(data.images, data.image);

      addBtn.disabled = !data.available;
      addLabel.textContent = data.available ? 'Add to Cart' : 'Sold out';

      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      panelMedia.querySelectorAll('img')[0] && (panelMedia.querySelectorAll('img')[0].alt = data.title);

      if (mode === 'popup') {
        // Reading offsetWidth/offsetHeight inside positionPopup forces a
        // synchronous layout, so this is safe to call immediately after
        // panel.hidden = false above (no need to wait a frame).
        positionPopup(pin);
        if (backdrop) {
          backdrop.hidden = false;
          requestAnimationFrame(function () { backdrop.classList.add('is-visible'); });
        }
      }
    }

    function closePanel() {
      if (mode === 'spotlight') return; // spotlight panel stays visible, just goes idle
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
      if (backdrop) {
        backdrop.classList.remove('is-visible');
        backdrop.hidden = true;
      }
      if (activePin) {
        activePin.setAttribute('aria-expanded', 'false');
        activePin.classList.remove('is-active');
        activePin = null;
      }
    }

    pins.forEach(function (pin) {
      pin.addEventListener('click', function (e) {
        e.stopPropagation();
        if (mode === 'popup' && activePin === pin && !panel.hidden) {
          closePanel();
          return;
        }
        openPanel(pin);
      });
    });

    panelClose.addEventListener('click', closePanel);
    if (backdrop) backdrop.addEventListener('click', closePanel);

    document.addEventListener('click', function (e) {
      if (mode !== 'popup' || panel.hidden) return;
      if (!panel.contains(e.target) && !stage.contains(e.target)) closePanel();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closePanel();
    });

    qtyMinus.addEventListener('click', function () {
      var v = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
      qtyInput.value = v;
    });
    qtyPlus.addEventListener('click', function () {
      var v = (parseInt(qtyInput.value, 10) || 1) + 1;
      qtyInput.value = v;
    });

    addForm.addEventListener('submit', function (e) {
      e.preventDefault();
      if (addBtn.disabled) return;

      var addUrl = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js';
      var originalLabel = addLabel.textContent;
      addBtn.disabled = true;
      addLabel.textContent = 'Adding…';

      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: variantInput.value,
          quantity: parseInt(qtyInput.value, 10) || 1
        })
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (err) { throw err; });
          return res.json();
        })
        .then(function () {
          addLabel.textContent = 'Added!';
          document.dispatchEvent(new CustomEvent('cart:updated'));
          setTimeout(function () {
            addLabel.textContent = originalLabel;
            addBtn.disabled = false;
          }, 1500);
        })
        .catch(function (err) {
          addLabel.textContent = (err && err.description) || 'Error adding item';
          setTimeout(function () {
            addLabel.textContent = originalLabel;
            addBtn.disabled = false;
          }, 2000);
        });
    });

    // --- Theme-editor-only position finder -------------------------------
    var finderToggle = root.querySelector('[data-hotspot-finder-toggle]');
    var finderReadout = root.querySelector('[data-hotspot-finder-readout]');
    if (finderToggle) {
      var finding = false;
      finderToggle.addEventListener('click', function () {
        finding = !finding;
        finderToggle.classList.toggle('is-active', finding);
        stage.classList.toggle('is-finding', finding);
        finderReadout.hidden = !finding;
      });

      stage.addEventListener('click', function (e) {
        if (!finding) return;
        if (e.target.closest('[data-hotspot-pin]')) return;
        var rect = stage.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width) * 100;
        var y = ((e.clientY - rect.top) / rect.height) * 100;
        finderReadout.textContent = 'X: ' + Math.round(x) + '%   Y: ' + Math.round(y) + '%';
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-hotspot-diagram]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when the section is re-rendered inside the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-hotspot-diagram]');
    if (el) init(el);
  });
})();
